import React, { useEffect, useRef, useState } from "react";
import "./mobile.css";
import MobileAuthPage from "./MobileAuthPage";
import MobileSettingsTab from "./MobileSettingsTab";
import {
  dropboxStartFolder,
  getDropboxStatus,
  openDropboxFile,
  takeDropboxReturn,
  DROPBOX_OFF,
  type DropboxReturn,
  type DropboxStatus,
} from "../shared/dropboxClient";
import DropboxFolderPicker from "./components/DropboxFolderPicker";
import BottomTabBar, { MobileTab } from "./components/BottomTabBar";
import MobilePageHeader from "./components/MobilePageHeader";
import EstimatorMobile from "./EstimatorMobile";
import MobileHomeTab, {
  summarizeDraft,
  type MobileDraftSummary,
} from "./MobileHomeTab";
import MobileProposalPage from "./MobileProposalPage";
import { supabase } from "../supabaseClient";
import { wipeLocalCompanyData } from "../shared/accessWipe";
import AccessRevoked from "../AccessRevoked";
import {
  AppUserSettings,
  DEFAULT_SETTINGS,
  fetchSettings,
  fetchOrgLogo,
  readCachedSettings,
} from "../settings";
import {
  loadCloudDraft,
  saveCloudDraft,
  type DraftSnapshot,
} from "../shared/draft";
import { parseEstimateFileText } from "../shared/estimateFile";
import { getRecents, pushRecent, type RecentFile } from "../shared/recents";
import type { PricingItemRow } from "../shared/pricing";
import { flushEmailQueue } from "../emailQueue";
import { canImportFromZoho } from "../shared/zohoImport";
import { dlog } from "../debugLog";
import { isRealSignIn } from "./authEvents";

// Auth flow: the gate at the bottom of render() shows MobileAuthPage
// (mobile-styled, single column) when there is no session. After login
// the onAuthStateChange subscription flips `session` to non-null and
// the bootstrap effect re-runs settings fetch automatically. The
// Settings tab now hosts a Log Out action (MobileSettingsTab) that
// calls supabase.auth.signOut() — the same subscription catches
// SIGNED_OUT and the gate returns the user to the login screen.

// ----------------------------------------------------------------------------
// Legacy localStorage cache lookup.
// ContractPage.tsx (line 283) reads user settings from any of three legacy
// keys — older releases wrote under different names. settings.ts's
// readCachedSettings only checks the current key ("du_user_settings"), so on
// a phone whose Supabase user_settings row is sparse we miss values the user
// already typed on desktop. Mirroring ContractPage's fallback chain here
// gives the mobile proposal screen the same data desktop sees, without
// touching the shared settings storage layer.
// ----------------------------------------------------------------------------
const LEGACY_SETTINGS_KEYS = [
  "userSettings",
  "du_user_settings",
  "duUserSettings",
];
function readAnyCachedSettings(): Partial<AppUserSettings> {
  if (typeof window === "undefined") return {};
  const merged: Record<string, any> = {};
  for (const key of LEGACY_SETTINGS_KEYS) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === "object") {
        // Keep the first non-empty value seen per field, in order of
        // key precedence above. Empty strings don't override a real value.
        for (const [k, v] of Object.entries(parsed)) {
          if (merged[k] !== undefined && merged[k] !== "" && merged[k] !== null)
            continue;
          merged[k] = v;
        }
      }
    } catch {
      // Ignore corrupt entries.
    }
  }
  return merged as Partial<AppUserSettings>;
}

/**
 * MobileApp — iPhone shell. Only rendered when window.matchMedia(
 * '(max-width: 480px)') matches. Desktop layout is untouched.
 *
 * Round 1 only the "New" tab is wired (renders EstimatorMobile).
 * Estimates / Customers / Settings show a Coming Soon placeholder.
 *
 * Session bootstrap (mobile-ui-fixes / mobile proposal round 1):
 * Resolves orgId via org_members lookup + loads user_settings, the
 * two pieces of state Round 2's proposal/send sheet will need. Held
 * here at the shell level so any future tab (proposal review, send
 * sheet, settings) can consume them via props without prop-drilling
 * through EstimatorMobile.
 *
 * Round 2 navigation: when the user taps "Review & Send" inside
 * EstimatorMobile, EstimatorMobile passes its current snapshot +
 * pricing + computed estimate up via onRequestReview. We stash that
 * payload and swap the screen to MobileProposalPage. The back button
 * in the proposal header drops us back to the estimator with state
 * intact.
 */
type ReviewPayload = {
  snapshot: DraftSnapshot;
  pricingItems: PricingItemRow[];
  estimate: any;
};

type MobileView = "tabs" | "proposal";

// Coming back from Dropbox's Allow page: read (and strip from the address
// bar) the code the callback appended. Captured once at module load so a
// re-render never replays it; Settings finishes the connect.
const dropboxReturnAtLoad: DropboxReturn | null = takeDropboxReturn();

export default function MobileApp() {
  // Land on Home. Every route that loads real work — Continue, New, a recent,
  // a file picked out of Files/Dropbox — switches to "new" itself, so this only
  // changes where a cold start puts you.
  const [tab, setTab] = useState<MobileTab>(() =>
    dropboxReturnAtLoad ? "settings" : "home"
  );
  const [view, setView] = useState<MobileView>("tabs");
  const [reviewPayload, setReviewPayload] = useState<ReviewPayload | null>(
    null
  );

  // Proposal → estimator patch signal. The proposal screen edits SOW /
  // Project Description on a payload copy; on return we push those edits into
  // EstimatorMobile's snapshot (the source of truth + the cloud writer) so a
  // later "Review & Send" rebuilds the payload WITH them instead of wiping
  // them, and so an estimator save can't clobber them in the cloud. The nonce
  // makes it an imperative one-shot EstimatorMobile watches.
  const [estimatorPatch, setEstimatorPatch] = useState<{
    patch: Partial<DraftSnapshot>;
    nonce: number;
  } | null>(null);

  // Bumped each time the user taps "New" while already on the estimator —
  // an imperative signal to EstimatorMobile to start a fresh estimate. The
  // unexported-work guard lives in EstimatorMobile, which owns that state.
  const [newEstimateNonce, setNewEstimateNonce] = useState(0);
  // The last "New" tap EstimatorMobile has applied. Kept here, not in
  // EstimatorMobile, because that screen unmounts on the Home tab — a tap on
  // the Home card has to survive until the estimate screen mounts and loads.
  const [handledNewEstimateNonce, setHandledNewEstimateNonce] = useState(0);

  // Bottom-nav handler — a plain tab switch. (Starting a fresh estimate used
  // to be a hidden "tap Estimate while on Estimate" gesture; it now lives in a
  // visible header "New" button, see handleNewEstimate.)
  const handleTabChange = (next: MobileTab) => {
    setTab(next);
  };

  // Header "New" button. Bumps the nonce EstimatorMobile watches; that owns the
  // unexported-work guard (confirm before wiping an un-exported estimate) and
  // the actual reset, so the confirm still fires exactly as before.
  const handleNewEstimate = () => {
    setNewEstimateNonce((n) => n + 1);
  };

  // ---- Open a .est file (iOS Files → Dropbox) ------------------------
  //
  // The Dropbox app registers as a Location in the iOS Files provider, so the
  // plain file input below reaches Dropbox with no Dropbox API, no OAuth and
  // no tokens — the same "let the OS do it" approach as the desktop button.
  //
  // Same imperative-nonce handoff as newEstimateNonce: the file is read and
  // validated here, then EstimatorMobile (which owns `snapshot`) applies it.
  const openFileInputRef = useRef<HTMLInputElement | null>(null);
  const [openedFile, setOpenedFile] = useState<{
    snapshot: any;
    fileName: string;
    nonce: number;
    /** Set when it came from Dropbox: Save to Dropbox goes back here. */
    dropboxPath?: string;
  } | null>(null);

  // Open from Dropbox: when this login has Dropbox connected, Open (Home card
  // or the Estimate header) shows the Dropbox browser, starting in the save
  // folder, instead of the phone's file picker. Status is re-read on every
  // tab switch, so a connect/disconnect in Settings applies right away.
  const [dropbox, setDropbox] = useState<DropboxStatus>(DROPBOX_OFF);
  const [dropboxOpen, setDropboxOpen] = useState<{
    busy: boolean;
    message: string | null;
  } | null>(null);

  // Must run inside the tap. Calling .click() from an effect or a state
  // callback loses the user-gesture context and iOS silently ignores it.
  const handleOpenFileTap = () => {
    if (dropbox.available && dropbox.connected) {
      setDropboxOpen({ busy: false, message: null });
      return;
    }
    openFileInputRef.current?.click();
  };

  /** "Open from phone files instead" — still inside the tap, so iOS allows
   *  the hidden input's click(). */
  const handlePhoneFilesTap = () => {
    setDropboxOpen(null);
    openFileInputRef.current?.click();
  };

  const handleDropboxFilePicked = async (path: string) => {
    setDropboxOpen({ busy: true, message: null });
    const r = await openDropboxFile(supabase, path);
    if (!r.ok) {
      setDropboxOpen({ busy: false, message: r.error });
      return;
    }
    const result = parseEstimateFileText(r.data.content);
    if (!result.ok) {
      setDropboxOpen({ busy: false, message: result.error });
      return;
    }
    setDropboxOpen(null);
    setOpenedFile((prev) => ({
      snapshot: result.snapshot,
      fileName: r.data.name,
      nonce: (prev?.nonce || 0) + 1,
      dropboxPath: r.data.path,
    }));
    const recentName =
      String((result.snapshot as any)?.estimateName || "").trim() ||
      r.data.name.replace(/\.(est|duest|json)$/i, "");
    pushRecent(recentName, result.snapshot);
    setRecents(getRecents());
    setTab("new");
  };

  const handleOpenFilePicked = async (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = e.target.files?.[0];
    // Reset immediately so picking the SAME file twice still fires onChange.
    e.target.value = "";
    if (!file) return;

    let text: string;
    try {
      text = await file.text();
    } catch (err) {
      // iOS throws here when a Dropbox file is online-only and the download
      // fails (airplane mode, no signal) — the picker hands back a File whose
      // bytes never materialize. Name the actual cause; "corrupted" would send
      // Jay hunting the wrong problem.
      console.error("[open] could not read picked file", err);
      window.alert(
        `Couldn't read "${file.name}".\n\n` +
          "If it's in Dropbox, it may not be downloaded to this phone yet — " +
          "open it once in the Dropbox app, then try again."
      );
      return;
    }

    const result = parseEstimateFileText(text);
    if (!result.ok) {
      window.alert(result.error);
      return;
    }

    setOpenedFile((prev) => ({
      snapshot: result.snapshot,
      fileName: file.name,
      nonce: (prev?.nonce || 0) + 1,
    }));

    // Feed the shared recents list so Home fills up on this phone the same way
    // it does on desktop, and name the entry the way desktop does: the
    // estimate's own name when it has one, else the file name sans extension.
    const recentName =
      String((result.snapshot as any)?.estimateName || "").trim() ||
      file.name.replace(/\.(est|duest|json)$/i, "");
    pushRecent(recentName, result.snapshot);
    setRecents(getRecents());
    // An opened file belongs on the estimator, wherever the user tapped from.
    setTab("new");
  };

  // -----------------------------------------------------------------
  // Auth gate (Option A) — mirrors the desktop App.tsx:820-825 pattern.
  // `session` is owned here so the gate at the bottom of render and the
  // bootstrap effect both see the same value. `authReady` defers the
  // first paint past the initial getSession() — without it, a logged-in
  // user with a token in localStorage would flash AuthPage for a frame
  // while supabase-js was still hydrating the session.
  // -----------------------------------------------------------------
  const [session, setSession] = useState<any>(null);
  const [authReady, setAuthReady] = useState(false);

  const [userId, setUserId] = useState<string | null>(null);
  const [orgId, setOrgId] = useState<string | null>(null);
  // The server answered "no company" for this login (e.g. "Remove access").
  const [accessRemoved, setAccessRemoved] = useState(false);

  // ---- Home tab ------------------------------------------------------
  // Recents are local to this device; the estimate in progress lives only in
  // the cloud draft, so the Continue card has to fetch it.
  const [recents, setRecents] = useState<RecentFile[]>(() => getRecents());
  const [draftSummary, setDraftSummary] = useState<MobileDraftSummary | null>(
    null
  );
  // Only true for the FIRST fetch. Later visits refresh in the background and
  // swap the card contents — flashing a skeleton over a card the user is
  // already looking at reads as a glitch.
  const [draftFirstLoad, setDraftFirstLoad] = useState(true);

  // Refresh both lists every time Home comes into view: the user may have just
  // come back from editing, and a Continue card naming the previous customer
  // would be worse than no card at all.
  useEffect(() => {
    if (tab !== "home") return;
    setRecents(getRecents());
    if (!userId) return;
    let cancelled = false;
    void loadCloudDraft(supabase, userId)
      .then((snap) => {
        if (!cancelled) setDraftSummary(summarizeDraft(snap));
      })
      .finally(() => {
        if (!cancelled) setDraftFirstLoad(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, userId]);

  const [userSettings, setUserSettings] = useState<AppUserSettings>(() => {
    // Merge order (lowest → highest precedence):
    //   DEFAULT_SETTINGS  →  legacy localStorage keys  →  current cache
    // Legacy keys cover phones that have data under an old key (`userSettings`,
    // `duUserSettings`) so userEmail/userPhone/logoDataUrl aren't blank on
    // first load — the proposal screen renders the same values desktop does
    // even before the cloud fetch completes.
    const legacy = readAnyCachedSettings();
    const current = readCachedSettings() || {};
    return {
      ...DEFAULT_SETTINGS,
      ...legacy,
      ...current,
    } as AppUserSettings;
  });

  // -----------------------------------------------------------------
  // Auth subscription. One-shot getSession() to seed the initial value,
  // then onAuthStateChange to react to sign-in/sign-out (so logging in
  // from AuthPage immediately re-runs the settings bootstrap below
  // instead of requiring a manual refresh — that's the CRITICAL bit
  // missing before this round).
  //
  // Both code paths flip `authReady` so the render gate can distinguish
  // "still resolving the stored token" from "definitely no session."
  // -----------------------------------------------------------------
  // Who is signed in as far as this screen knows, and whether startup has
  // finished resolving the stored login. Together they tell a real login
  // (from the login screen) apart from the SIGNED_IN that supabase-js also
  // fires when it restores the session at cold start and every time the app
  // comes back to the foreground.
  const signedInUserRef = useRef<string | null>(null);
  const authResolvedRef = useRef(false);
  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const { data } = await supabase.auth.getSession();
        if (cancelled) return;
        signedInUserRef.current = data?.session?.user?.id ?? null;
        setSession(data?.session ?? null);
      } catch (e: any) {
        // eslint-disable-next-line no-console
        console.warn("[mobile-auth] getSession threw:", e?.message || e);
      } finally {
        if (!cancelled) {
          authResolvedRef.current = true;
          setAuthReady(true);
        }
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((event, sess) => {
      const prevUserId = signedInUserRef.current;
      const nextUserId = sess?.user?.id ?? null;
      signedInUserRef.current = nextUserId;
      if (event === "INITIAL_SESSION") authResolvedRef.current = true;
      const isRealLogin = isRealSignIn(
        event,
        authResolvedRef.current,
        prevUserId,
        nextUserId
      );
      dlog("[mobile-auth] state change:", event, {
        hasSession: !!sess,
        userId: sess?.user?.id || null,
      });
      setSession(sess ?? null);
      setAuthReady(true);
      // Post-login UX: always land on the estimator (the "New" tab) and
      // make sure we're not still stuck inside a leftover proposal
      // overlay. Without this, a user who logs out from the Settings tab
      // and then logs back in would land on Settings again because the
      // tab/view state survived the auth gate (MobileApp itself doesn't
      // unmount across login).
      // ...except right after returning from Dropbox: Settings is finishing
      // the connect and must stay on screen.
      // Only on a real login: the session-restore SIGNED_IN used to yank the
      // app to the Estimate tab on every launch and every return to the app.
      if (isRealLogin && !dropboxReturnAtLoad) {
        setTab("new");
        setView("tabs");
        setReviewPayload(null);
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  // Live connectivity, mirrored from the browser. Drives the Zoho import
  // entry (online-only) on the Customer card.
  const [isOnline, setIsOnline] = useState<boolean>(
    typeof navigator === "undefined" ? true : navigator.onLine !== false
  );
  useEffect(() => {
    const goOnline = () => setIsOnline(true);
    const goOffline = () => setIsOnline(false);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  // -----------------------------------------------------------------
  // Offline email-queue flush. MobileSendSheet drops failed/offline
  // sends into du_email_queue_v1 (src/emailQueue.ts). Try once at
  // mount and again whenever the device transitions back online so
  // queued proposals go out without a manual retry.
  // -----------------------------------------------------------------
  useEffect(() => {
    const tryFlush = () => {
      if (typeof navigator !== "undefined" && navigator.onLine === false)
        return;
      // fallbackReplyTo rescues items queued by builds that stored no replyTo:
      // send-proposal-email 400s without one, so those sat in the outbox
      // failing on every flush while the user was told they had been sent.
      void flushEmailQueue(supabase, {
        fallbackReplyTo: (userSettings.userEmail || "").trim(),
      })
        .then((r) => {
          if (r.sent > 0) {
            dlog("[mobile] email queue flushed", r);
          }
          // A stuck outbox used to be completely silent. Warn — this is a
          // proposal the customer has not received.
          if (r.remaining > 0) {
            // eslint-disable-next-line no-console
            console.warn(
              `[mobile] ${r.remaining} queued proposal email(s) still unsent`,
              r.errors
            );
          }
        })
        .catch(() => {
          /* swallow — queue keeps the items */
        });
    };
    tryFlush();
    window.addEventListener("online", tryFlush);
    return () => {
      window.removeEventListener("online", tryFlush);
    };
    // Re-run once settings arrive so the fallback address is available.
  }, [userSettings.userEmail]);

  // -----------------------------------------------------------------
  // Bootstrap: orgId (org_members) → userSettings. Keyed on the
  // session.user.id from the auth subscription above, so signing in
  // from AuthPage triggers a fresh bootstrap automatically.
  // -----------------------------------------------------------------
  useEffect(() => {
    let cancelled = false;

    const uid = session?.user?.id || null;
    const sessionEmail = (session?.user?.email || "").trim();
    setUserId(uid);

    if (!uid) {
      // No session — clear the previously-resolved org so a logout from
      // a future Settings tab cleanly wipes downstream state.
      setOrgId(null);
      return;
    }

    dlog("[mobile-bootstrap] session", {
      userId: uid,
      hasSession: !!session,
      sessionEmail,
    });

    (async () => {
      // Even before the cloud fetch resolves, fold the auth email into
      // userSettings as a fallback for userEmail — desktop's AppShell
      // does the same thing with `userEmail={email}` (App.tsx:870), and
      // the proposal screen's "Prepared By" block needs it populated.
      if (sessionEmail) {
        setUserSettings((prev) => {
          if ((prev.userEmail || "").trim()) return prev;
          return { ...prev, userEmail: sessionEmail };
        });
      }

      // org_members lookup mirrors AuthedApp's resolution in App.tsx —
      // primary key is org_id, with a fallback to account_id for older
      // schemas. Whichever returns a row wins.
      let resolvedOrgId: string | null = null;
      let orgLookupErr = "";
      try {
        const q1 = await supabase
          .from("org_members")
          .select("org_id, role")
          .eq("user_id", uid)
          .maybeSingle();
        if (!q1.error && q1.data?.org_id) {
          resolvedOrgId = q1.data.org_id as string;
        } else {
          if (q1.error) orgLookupErr = `q1: ${q1.error.message}`;
          const q2 = await supabase
            .from("org_members")
            .select("account_id, role")
            .eq("user_id", uid)
            .maybeSingle();
          if (!q2.error && (q2.data as any)?.account_id) {
            resolvedOrgId = (q2.data as any).account_id as string;
          } else if (q2.error) {
            orgLookupErr =
              (orgLookupErr ? orgLookupErr + " | " : "") +
              `q2: ${q2.error.message}`;
          }
        }
      } catch (err: any) {
        orgLookupErr = err?.message || String(err);
        // eslint-disable-next-line no-console
        console.warn("[mobile-bootstrap] org lookup failed", err);
      }
      // No membership (and the lookup itself worked): someone invited who
      // closed the set-password page before finishing still has an open
      // invite. Accept it by their verified email — same fallback as the
      // desktop shell (App.tsx loadOrgForUser).
      if (!resolvedOrgId && !orgLookupErr && !cancelled) {
        try {
          const { data } = await supabase.functions.invoke("accept-invite", {
            body: {},
          });
          if ((data as any)?.accepted && (data as any)?.account_id) {
            resolvedOrgId = String((data as any).account_id);
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.warn("[mobile-bootstrap] accept-invite fallback failed", err);
        }
      }
      if (cancelled) return;
      // The server ANSWERED: no company (e.g. an admin used "Remove access").
      // Forget this device's copy of company data. A failed lookup
      // (orgLookupErr) is not an answer and wipes nothing.
      if (!resolvedOrgId && !orgLookupErr) {
        wipeLocalCompanyData();
      }
      setAccessRemoved(!resolvedOrgId && !orgLookupErr);
      setOrgId(resolvedOrgId);
      dlog("[mobile-bootstrap] orgId", resolvedOrgId, {
        lookupError: orgLookupErr || null,
      });

      if (!resolvedOrgId) return;

      // ---- fetchSettings call-site timing wrapper ----
      // settings.ts logs per-leg outcomes via [fetchSettings] lines; this
      // adds whether the call returned or threw and how long it took.
      const tFetch =
        typeof performance !== "undefined" ? performance.now() : Date.now();
      let fetched: AppUserSettings | null = null;
      try {
        fetched = await fetchSettings(resolvedOrgId, uid);
        const ms = Math.round(
          (typeof performance !== "undefined" ? performance.now() : Date.now()) -
            tFetch
        );
        if (cancelled) return;
        dlog("[mobile-bootstrap] fetchSettings OK in", ms, "ms", {
          hasUserName: !!fetched.userName,
          hasUserPhone: !!fetched.userPhone,
          hasUserEmail: !!fetched.userEmail,
          hasCompanyName: !!fetched.companyName,
          hasLogo: !!fetched.logoDataUrl,
          logoLen: (fetched.logoDataUrl || "").length,
        });
      } catch (err: any) {
        const ms = Math.round(
          (typeof performance !== "undefined" ? performance.now() : Date.now()) -
            tFetch
        );
        // eslint-disable-next-line no-console
        console.warn("[mobile-bootstrap] fetchSettings THREW after", ms, "ms", {
          name: err?.name,
          message: err?.message,
        });
      }

      if (fetched && !cancelled) {
        // Per-field merge: only let the cloud value win when it's
        // non-empty. fetchSettings normalizes missing columns to "" /
        // null, and a naive spread would wipe out userPhone/userEmail/
        // logoDataUrl that we already populated from cache or session.
        setUserSettings((prev) => {
          const merged: AppUserSettings = { ...prev };
          for (const [k, v] of Object.entries(fetched as AppUserSettings)) {
            if (v === undefined) continue;
            if (typeof v === "string" && v.trim() === "") continue;
            if (Array.isArray(v) && v.length === 0 && Array.isArray((prev as any)[k]) && (prev as any)[k].length > 0)
              continue;
            (merged as any)[k] = v;
          }
          // userEmail fallback chain: cloud → existing (cache/session) →
          // session — mirrors desktop's effective behavior.
          if (!String(merged.userEmail || "").trim() && sessionEmail) {
            merged.userEmail = sessionEmail;
          }
          return merged;
        });
      }

      // ---- Lazy logo retry (Step 2) -----------------------------------
      // The logo leg inside fetchSettings has an 8s timeout so it can't
      // block phone/email/company text any longer. If the first attempt
      // didn't produce a logo, kick off one more attempt in the
      // background — this one is allowed to take as long as the network
      // needs, and just patches logoDataUrl into state when (if) it
      // lands. A bad signal at boot no longer means a logo-less proposal
      // for the rest of the session.
      if (!cancelled && !((fetched && fetched.logoDataUrl) || "")) {
        dlog("[mobile-bootstrap] lazy logo retry starting");
        const tLazy =
          typeof performance !== "undefined" ? performance.now() : Date.now();
        void fetchOrgLogo(resolvedOrgId).then((logo) => {
          const ms = Math.round(
            (typeof performance !== "undefined"
              ? performance.now()
              : Date.now()) - tLazy
          );
          if (cancelled) return;
          if (!logo) {
            // eslint-disable-next-line no-console
            console.warn(
              "[mobile-bootstrap] lazy logo retry returned nothing after",
              ms,
              "ms"
            );
            return;
          }
          dlog(
            "[mobile-bootstrap] lazy logo retry OK in",
            ms,
            "ms, len:",
            logo.length
          );
          setUserSettings((prev) => ({ ...prev, logoDataUrl: logo }));
        }).catch((e: any) => {
          const ms = Math.round(
            (typeof performance !== "undefined"
              ? performance.now()
              : Date.now()) - tLazy
          );
          // eslint-disable-next-line no-console
          console.warn(
            "[mobile-bootstrap] lazy logo retry threw after",
            ms,
            "ms:",
            e?.message
          );
        });
      }
    })();

    return () => {
      cancelled = true;
    };
    // Re-runs whenever the auth session changes (sign-in/sign-out).
    // session.user.id is the only thing the bootstrap branches on; the
    // session object itself is unstable across re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session?.user?.id]);

  const handleRequestReview = (payload: ReviewPayload) => {
    setReviewPayload(payload);
    setView("proposal");
    dlog("[mobile] navigating to proposal screen", {
      estimateId: payload.snapshot?.estimateId,
      finalEstimate: payload.estimate?.finalEstimate,
    });
  };

  const handleBackToEstimator = () => {
    // Carry the proposal's SOW / Project Description edits back into the
    // estimator's snapshot so they survive a re-entry via "Review & Send"
    // (which rebuilds the payload from that snapshot) and don't get clobbered
    // by a later estimator cloud save.
    const s = reviewPayload?.snapshot;
    if (s) {
      setEstimatorPatch((prev) => ({
        patch: {
          sowText: s.sowText,
          sowTouched: s.sowTouched,
          projectDescription: s.projectDescription,
          projectDescriptionTouched: s.projectDescriptionTouched,
        },
        nonce: (prev?.nonce ?? 0) + 1,
      }));
    }
    setView("tabs");
  };

  // Used by the proposal screen when the sender-info gate fires —
  // routes the user straight to the Settings tab so they can fill
  // in name / phone / email and try again.
  const handleOpenSettings = () => {
    setView("tabs");
    setTab("settings");
  };

  // When the proposal page edits the snapshot (e.g. SOW), reflect the
  // change in the payload it sees, AND write through to the cloud draft
  // so the estimator picks it up on return.
  const handleProposalPatchSnapshot = (patch: Partial<DraftSnapshot>) => {
    setReviewPayload((prev) => {
      if (!prev) return prev;
      const nextSnap: DraftSnapshot = {
        ...prev.snapshot,
        ...patch,
        savedAt: new Date().toISOString(),
      };
      // Fire-and-forget cloud save — same table EstimatorMobile uses.
      if (userId && nextSnap.estimateId) {
        void saveCloudDraft(supabase, userId, nextSnap).catch(() => {
          // Cloud save failures are non-fatal — the local edit holds.
        });
      }
      return { ...prev, snapshot: nextSnap };
    });
  };

  const sessionUserId = session?.user?.id || "";
  useEffect(() => {
    if (!sessionUserId) return;
    let cancelled = false;
    void getDropboxStatus(supabase).then((st) => {
      if (!cancelled) setDropbox(st);
    });
    return () => {
      cancelled = true;
    };
  }, [sessionUserId, tab]);

  // ---------------- Screen for the active bottom tab
  // Kept as a plain function (not a sub-component) so the same React tree
  // is returned on every render — EstimatorMobile stays mounted even when
  // we toggle the proposal overlay's visibility.
  const screenForTab = () => {
    if (tab === "home") {
      return (
        <MobileHomeTab
          userName={userSettings.userName}
          companyName={userSettings.companyName}
          draft={draftSummary}
          draftLoading={draftFirstLoad && !!userId}
          recents={recents}
          onContinue={() => setTab("new")}
          onNewEstimate={() => {
            // Same imperative signal the header "New" button sends;
            // EstimatorMobile owns the un-exported-work guard and the reset.
            handleNewEstimate();
            setTab("new");
          }}
          onOpenFile={handleOpenFileTap}
          onOpenRecent={(rf) => {
            // Reuse the picked-file handoff: EstimatorMobile owns `snapshot`
            // and applies whatever arrives under a fresh nonce.
            setOpenedFile((prev) => ({
              snapshot: rf.json,
              fileName: rf.name,
              nonce: (prev?.nonce || 0) + 1,
            }));
            pushRecent(rf.name, rf.json);
            setRecents(getRecents());
            setTab("new");
          }}
        />
      );
    }
    if (tab === "new") {
      return (
        <EstimatorMobile
          orgId={orgId}
          userId={userId}
          userSettings={userSettings}
          onRequestReview={handleRequestReview}
          canImport={canImportFromZoho(session?.user?.email)}
          isOnline={isOnline}
          newEstimateNonce={newEstimateNonce}
          handledNewEstimateNonce={handledNewEstimateNonce}
          onNewEstimateHandled={setHandledNewEstimateNonce}
          externalPatch={estimatorPatch}
          openedFile={openedFile}
        />
      );
    }
    // settings
    return (
      <MobileSettingsTab
        userEmail={
          (userSettings.userEmail || "").trim() ||
          session?.user?.email ||
          null
        }
        userName={userSettings.userName || null}
        companyName={userSettings.companyName || null}
        dropboxReturn={dropboxReturnAtLoad}
      />
    );
  };

  // -----------------------------------------------------------------
  // Render — BOTH the tabs region AND the proposal region live in the
  // tree at all times once the user has triggered a review. Visibility
  // is toggled via CSS on data-view so EstimatorMobile keeps its full
  // in-memory state across "Review & Send" / "Back to Estimator" trips.
  // Re-mounting it would wipe customer info, line selections, qtys,
  // and the live total — the same class of bug we fixed in index.tsx
  // for the rotation remount.
  // -----------------------------------------------------------------
  // Auth render gate (Option A — see TODO at top of file). Mirrors the
  // desktop pattern in App.tsx:820-825.
  //
  // - While supabase-js is still hydrating the stored token, render a
  //   minimal placeholder so we don't flash AuthPage to a user who is
  //   actually signed in.
  // - With no session, show the existing desktop-styled AuthPage. The
  //   onAuthStateChange subscription above flips `session` as soon as
  //   signInWithPassword succeeds, which re-runs the settings bootstrap
  //   automatically.
  // -----------------------------------------------------------------
  if (!authReady) {
    return (
      <div
        className="mobile-app"
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: "var(--ink, #111)",
          fontSize: 12,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
        }}
      >
        Loading…
      </div>
    );
  }
  if (!session) {
    return <MobileAuthPage />;
  }

  if (accessRemoved) {
    return (
      <AccessRevoked
        removed
        onLogout={() => {
          supabase.auth.signOut().catch(() => {});
        }}
      />
    );
  }

  // Per-tab page title for the shell header. Replaces the previous
  // static "Estimator" header so the user always knows which tab
  // they're looking at — the cue that was missing before.
  const tabTitle =
    tab === "home" ? "Home" : tab === "new" ? "Estimate" : "Settings";

  // Proposal screen subtitle: "for Mr. Smith — Commack". Gives the
  // user immediate context for WHICH estimate's proposal they're on,
  // which they'd otherwise have to infer from the body content.
  const proposalSubtitle = (() => {
    const sn = reviewPayload?.snapshot;
    if (!sn) return undefined;
    const first = (sn.clientFirstName || "").trim();
    const last = (sn.clientLastName || "").trim();
    const town = (sn.clientTown || "").trim();
    const name = [first, last].filter(Boolean).join(" ");
    const tail = [name, town].filter(Boolean).join(" — ");
    return tail ? `for ${tail}` : undefined;
  })();

  return (
    <div className="mobile-app" data-active-tab={tab} data-view={view}>
      {/* Hidden Open input.

          NO accept= filter, deliberately. Safari maps accept extensions to
          UTIs, and ".est" is not a registered type — supplying it greys our
          own files out as unselectable in the iOS Files picker, which is the
          exact opposite of what this button is for. So we accept anything and
          validate the contents in parseEstimateFileText instead. The tradeoff
          is that the picker also shows unrelated files; picking one produces a
          plain-English error rather than a crash. */}
      <input
        ref={openFileInputRef}
        type="file"
        style={{ display: "none" }}
        onChange={handleOpenFilePicked}
      />

      {dropboxOpen ? (
        <DropboxFolderPicker
          mode="open"
          startPath={dropboxStartFolder(dropbox)}
          busy={dropboxOpen.busy}
          message={dropboxOpen.message}
          onCancel={() => setDropboxOpen(null)}
          onOpenFile={handleDropboxFilePicked}
          onPhoneFiles={handlePhoneFilesTap}
        />
      ) : null}

      {/* Tabs region (estimator + placeholder tabs). Always mounted. */}
      <div className="m-shell-region" data-region="tabs">
        <MobilePageHeader
          title={tabTitle}
          leftAction={
            tab === "new" ? (
              <div className="m-header-actions">
                <button
                  type="button"
                  className="m-header-new"
                  onClick={handleNewEstimate}
                  aria-label="Start a new estimate"
                >
                  <span className="m-header-new-plus" aria-hidden>
                    +
                  </span>
                  New
                </button>
                <button
                  type="button"
                  className="m-header-open"
                  onClick={handleOpenFileTap}
                  aria-label="Open an estimate file"
                >
                  Open
                </button>
              </div>
            ) : undefined
          }
        />

        {screenForTab()}

        <div className="m-bottom">
          <BottomTabBar active={tab} onChange={handleTabChange} />
        </div>
      </div>

      {/* Proposal region — mounted lazily once the user has triggered a
          review, then kept in the tree so its localStorage-derived state
          (notes, timeline, show-cost toggle) survives the trip back. */}
      {reviewPayload ? (
        <div className="m-shell-region" data-region="proposal">
          <MobilePageHeader
            title="Proposal"
            subtitle={proposalSubtitle}
            back={{ label: "Estimator", onClick: handleBackToEstimator }}
          />

          <MobileProposalPage
            snapshot={reviewPayload.snapshot}
            pricingItems={reviewPayload.pricingItems}
            estimate={reviewPayload.estimate}
            orgId={orgId}
            userSettings={userSettings}
            onBack={handleBackToEstimator}
            onOpenSettings={handleOpenSettings}
            onPatchSnapshot={handleProposalPatchSnapshot}
          />
        </div>
      ) : null}
    </div>
  );
}
